import ActionMenu from "Components/Common/ActionMenu";
import TableContainer from "Components/Common/TableContainer";
import Stepper from "Components/presetQuotes/Stepper";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col } from "reactstrap";
import ItemsFilter from "./ItemsFilter";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { createSelector } from "reselect";
import {
  findAndReplaceAddedItem,
  selectItemsState,
  setCalculatedTotalItems,
} from "slices/items/reducer";
import { deleteItem, editQuote, getItems } from "slices/thunks";
import ManageItemModal from "Components/Quotes/ManageQuotes/ManageItemModal";
import AddCategoryModal from "Components/Quotes/ManageQuotes/AddCategoryModal";
import AddSubCategoryModal from "Components/Quotes/ManageQuotes/AddSubCategoryModal";
import { ItemData } from "types/item.api";
import DiscountModal from "Components/Quotes/ManageQuotes/DiscountModal";
import DeleteItemModal from "Components/Modals/DeleteItemModal";
import TaxModal from "Components/Quotes/ManageQuotes/TaxModal";
import Icon from "Components/Icons/Icon";
import CheckMarginModal from "Components/Modals/CheckMarginModal";
import { PresetQuotePayload } from "types/preset.api";
import useItemCalculation from "Components/Hooks/useItemCalculation";
import { calculateMarginData } from "utils/utils";
import { ErrorHandler, ErrorToast } from "utils/toast";
import CustomButton from "Components/Custom/CustomButton";
import AddDepositModal, {
  DepositType,
} from "Components/Quotes/ManageQuotes/AddDepositModal";
import RestrictedAccessModal from "Components/Modals/RestrictedAccessModal";
import { useNavigate } from "react-router-dom";

interface props {
  onSubmit: () => void;
  isEdit?: boolean;
  presetQuote: PresetQuotePayload | null;
}

// Move selector outside component to prevent recreation

const ItemsPane = ({ onSubmit, isEdit = false, presetQuote = null }: props) => {
  const dispatch = useAppDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const quoteDetails = useAppSelector((state) => state.quote.quoteDetails);
  const [deleteModal, setDeleteModal] = useState<number | null>(null);
  const [quantity, setQuantity] = useState<number>(0);
  // console.log("quoteDetails", quoteDetails);
  const isQuoteEditLoading = useAppSelector((s) => s.quote.status.edit);

  const itemsSelector = createSelector(selectItemsState, (state) => ({
    items: state.items,
    isLoading: state.status.fetch,
    meta: state.pagination,
  }));

  const { items, meta, isLoading } = useAppSelector(itemsSelector);

  const [showAddCategoryModal, setShowAddCategoryModal] =
    useState<boolean>(false);
  const [showAddSubCategoryModal, setShowAddSubCategoryModal] =
    useState<boolean>(false);
  const [showItemFilter, setShowItemFilter] = useState<boolean>(false);
  const [searchText, setSearchText] = useState("");
  const [selectedItem, setSelectedItem] = useState<ItemData | null>(null);
  const [showManageItemModal, setShowManageItemModal] =
    useState<boolean>(false);
  const [isEditItemModal, setIsEditItemModal] = useState<boolean>(false);
  const [openMarginModal, setOpenMarginModal] = useState<boolean>(false);
  const [openDiscountModal, setOpenDiscountModal] = useState<boolean>(false);
  const [openTaxModal, setOpenTaxModal] = useState(false);
  const [openDepositModal, setOpenDepositModal] = useState(false);
  const user = useAppSelector((state) => state.auth.user);
  const [isShowStripeConnectModal, setIsShowStripeConnectModal] =
    useState(false);
  const navigate = useNavigate();
  const addItems = useAppSelector((state) => state.item.addItems);
  const { discount, setDiscount, setTax, tax, totals, taxOptions } =
    useItemCalculation(addItems);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<number[]>([]);
  const [depositDetails, setDepositDetails] = useState<{
    amount: number;
    type: DepositType;
    paymentMethod: "cash" | "stripe";
  } | null>(null);

  useEffect(() => {
    if (isEdit && quoteDetails) {
      if (quoteDetails.deposit_required) {
        setDepositDetails({
          amount: quoteDetails?.deposit_type === "percentage" ? quoteDetails.deposit_percentage || 0 : quoteDetails?.deposit_amount || 0,
          type:
            quoteDetails?.deposit_type === "percentage" ? "Percentage" : "Fixed",
          paymentMethod: ((quoteDetails.deposit_payment?.payment_method ?? quoteDetails.deposit_payment_method) as "cash" | "stripe") ?? "cash",
        });
      } else {
        setDepositDetails(null);
      }
    }
  }, [isEdit, quoteDetails]);

  // useEffect(() => {
  //   if (quoteDetails) {
  //     console.log("quoteDetails.id", quoteDetails.id);
  //     dispatch(
  //       getItems({
  //         ...(presetQuote
  //           ? { quote_template_id: presetQuote.id }
  //           : { quote_id: quoteDetails.id }),
  //       })
  //     );
  //   }
  // }, [dispatch, quoteDetails, presetQuote]);

  useEffect(() => {
    dispatch(
      setCalculatedTotalItems({
        subTotal: totals.subTotal,
        totalTax: totals.taxAmount,
        totalDiscount: totals.discountAmount,
        grandTotal: totals.grandTotal,
        totalDeposit:
          depositDetails?.type && depositDetails?.type === "Percentage"
            ? (depositDetails.amount / 100) * totals.grandTotal
            : depositDetails?.amount ?? 0,
        depositType: depositDetails?.type ?? null,
      }),
    );
  }, [totals, dispatch, depositDetails]);

  // Memoize all toggle functions with useCallback
  const toggleAddCategoryModal = useCallback(() => {
    setShowAddCategoryModal((prev) => !prev);
  }, []);

  const toggleAddSubCategoryModal = useCallback(() => {
    setShowAddSubCategoryModal((prev) => !prev);
  }, []);

  const toggleManageItemModal = useCallback(() => {
    setSelectedItem(null);
    setIsEditItemModal(false);
    setShowManageItemModal((prev) => !prev);
  }, []);

  const toggleMarginModal = useCallback(() => {
    setOpenMarginModal((prev) => !prev);
  }, []);

  const toggleDepositModal = useCallback(() => {
    setOpenDepositModal((prev) => !prev);
  }, []);

  const toggleDiscountModal = useCallback(() => {
    setOpenDiscountModal((prev) => !prev);
  }, []);

  // Memoize quantity change handler
  const handleQuantityChange = useCallback(
    (item: ItemData, quantity: number) => {
      dispatch(findAndReplaceAddedItem({ ...item, quantity }));
      setQuantity(quantity);
    },
    [dispatch],
  );

  // Memoize edit handler
  const handleEditItem = useCallback(
    (rowData: ItemData) => {
      setSelectedItem({ ...rowData, quantity });
      setIsEditItemModal(true);
      setShowManageItemModal(true);
    },
    [quantity],
  );

  const handleDeleteItem = useCallback((rowData: ItemData) => {
    setDeleteModal(rowData.id);
  }, []);

  // Memoize columns with stable dependencies
  const itemsColumn = useMemo(
    () => [
      {
        header: "ITEM NAME",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "CATEGORY",
        accessorKey: "category_name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "SUBCATEGORY",
        accessorKey: "subcategory_name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary ">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "UNIT",
        accessorKey: "unit",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "PRICE/UNIT",
        accessorKey: "price",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            £{Number(cell.getValue() ?? 0).toFixed(2)}
          </span>
        ),
      },
      {
        header: "QUANTITY",
        accessorKey: "quantity",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          const foundItem = addItems.find((item) => item.id === rowData.id);
          let currentQuantity = 0;
          if (foundItem) {
            currentQuantity = foundItem.quantity;
          }

          return (
            <Stepper
              isEdit={isEdit}
              initialQuantity={currentQuantity}
              key={`${rowData.id}-${currentQuantity}`}
              onQuantityChange={(quantity) =>
                handleQuantityChange(rowData, quantity)
              }
            />
          );
        },
      },
      {
        header: "TOTAL",
        accessorKey: "total",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          const foundItem = addItems.find((item) => item.id === rowData.id);
          let currentQuantity = 0;
          if (foundItem) {
            currentQuantity = foundItem.quantity;
          }

          const total = Number(currentQuantity) * Number(rowData.price);

          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              £{total.toFixed(2)}
            </span>
          );
        },
      },
      {
        header: "ACTIONS",
        cell: (cell: any) => (
          <ActionMenu
            onEdit={() => handleEditItem(cell.row.original)}
            onDelete={() => handleDeleteItem(cell.row.original)}
          />
        ),
      },
    ],
    [addItems, isEdit, handleQuantityChange, handleEditItem, handleDeleteItem],
  );

  const handleSearch = useCallback((e: string) => {
    setSearchText(e);
  }, []);

  useEffect(() => {
    const params = searchText ? { search: searchText } : { page: currentPage };
    dispatch(
      getItems({
        ...params,
        ...(selectedCategory ? { category_id: Number(selectedCategory) } : {}),
        ...(selectedSubcategory.length > 0
          ? { subcategory_ids: selectedSubcategory }
          : {}),
        ...(presetQuote
          ? { quote_template_id: presetQuote.id }
          : { quote_id: quoteDetails?.id }),
      }),
    );
  }, [
    currentPage,
    dispatch,
    presetQuote,
    quoteDetails?.id,
    searchText,
    selectedCategory,
    selectedSubcategory,
  ]);

  const handleSave = async () => {
    if (quoteDetails) {
      if (addItems.length === 0) {
        ErrorToast("Please select item to proceed further.");
        return;
      }

      try {
        await dispatch(
          editQuote({
            quote_id: quoteDetails?.id,
            items: addItems,
            vat_setting_id: tax,
            discount: discount,
            deposit_required: depositDetails ? 1 : 0,
            deposit_amount: depositDetails
              ? depositDetails.type === "Fixed"
                ? Number(depositDetails.amount ?? 0)
                : Number(
                  (
                    ((depositDetails.amount ?? 0) / 100) *
                    (totals?.grandTotal ?? 0)
                  ).toFixed(2),
                )
              : 0,
            deposit_percentage:
              depositDetails?.type === "Percentage"
                ? depositDetails.amount
                : null,
            deposit_type: depositDetails
              ? depositDetails.type === "Fixed"
                ? "fixed"
                : "percentage"
              : undefined,
            deposit_payment_method: depositDetails?.paymentMethod ?? "cash",
          }),
        )?.unwrap();
        onSubmit();
      } catch (error) {
        ErrorHandler(error);
      }
    }
  };

  useEffect(() => {
    const isAnyModalOpen =
      showManageItemModal ||
      openMarginModal ||
      showItemFilter ||
      deleteModal !== null ||
      isEditItemModal ||
      showAddCategoryModal ||
      showAddSubCategoryModal;
    if (isAnyModalOpen) {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    }

    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, [
    deleteModal,
    isEditItemModal,
    openMarginModal,
    showAddCategoryModal,
    showAddSubCategoryModal,
    showItemFilter,
    showManageItemModal,
  ]);

  const isDepositEditable = useMemo(
    () =>
    (quoteDetails?.deposit_payment
      ? quoteDetails.deposit_payment.status !== "received"
      : quoteDetails?.payment_status !== "received"),
    [quoteDetails?.deposit_payment, quoteDetails?.payment_status]
  );


  return (
    <>
      <div className="mt-2" id="quoteItemTab">
        <TableContainer
          tableId="quoteItemTab"
          columns={itemsColumn}
          data={items}
          isGlobalFilter={true}
          customPageSize={meta?.per_page}
          divClass="table-responsive table-card mb-2 bg-white"
          tableClass="align-middle table-nowrap table-borderless"
          thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5 category-table "
          theadClass="table-light"
          SearchPlaceholder="Search items"
          searchAlign="left"
          isQuoteFilter={true}
          currentPage={currentPage}
          totalPages={meta?.last_page}
          toPage={meta?.to}
          fromPage={meta?.from}
          totalItems={meta?.total}
          onAddItemClick={toggleManageItemModal}
          onItemFilterClick={() => setShowItemFilter(true)}
          onPageChange={setCurrentPage}
          onSearch={handleSearch}
          isInitialLoading={isLoading === "loading" && !items?.length}
          isPaginationLoading={isLoading === "loading" && items?.length > 0}
        />
        <Col lg={12}>
          <div className="d-flex align-items-start justify-content-between w-100 pt-4 mt-4 border-top border-top border-top-dashed">
            <CustomButton
              className="border-7 bg-primary w-15"
              text="Save"
              onClick={handleSave}
              loading={isQuoteEditLoading === "loading"}
            />

            <td colSpan={6} className="fw-medium p-0">
              <table className="table table-borderless mb-0">
                <tbody>
                  <tr className="d-flex justify-content-between gap-5">
                    <td className="text-grey89 fw-normal fs-14 font-primary">
                      Sub Total
                    </td>
                    <td className="text-end fw-normal fs-14 font-primary text-black2D">
                      £{Number(totals?.subTotal ?? 0).toFixed(2)}
                    </td>
                  </tr>
                  <tr className="d-flex justify-content-between gap-5">
                    <td className="text-grey89 fw-normal fs-14 font-primary">
                      Margin (
                      {Number(
                        calculateMarginData(addItems).totals
                          .totalMarginPercent ?? 0,
                      ).toFixed(2)}
                      %)
                    </td>
                    <td className="text-end">
                      <p
                        className="text-decoration-underline text-primary fw-normal fs-14 font-primary cursor-pointer mb-0 pb-0"
                        onClick={toggleMarginModal}
                      >
                        Check Margin
                      </p>
                    </td>
                  </tr>
                  <tr className="d-flex justify-content-between gap-5">
                    <td className="text-grey89 fw-normal fs-14 font-primary">
                      Tax ({totals?.taxPercentage}%)
                    </td>
                    <td
                      className="text-primary cursor-pointer mb-0 pb-0 fw-normal fs-14 font-primary"
                      onClick={() => setOpenTaxModal(true)}
                    >
                      <Icon name="Edit" />£
                      {Number(totals?.taxAmount ?? 0).toFixed(2)}
                    </td>
                  </tr>
                  <tr className="d-flex justify-content-between gap-5">
                    <td className="text-grey89 fw-normal fs-14 font-primary">
                      Discount ({discount + "%"})
                    </td>
                    <td className="text-end">
                      <p
                        className="text-primary cursor-pointer mb-0 pb-0 fw-normal fs-14 font-primary"
                        onClick={toggleDiscountModal}
                      >
                        <Icon name="Edit" />£
                        {Number(totals?.discountAmount ?? 0).toFixed(2)}
                      </p>
                    </td>
                  </tr>
                  <tr className="border-top border-top-dashed d-flex justify-content-between gap-5">
                    <th
                      scope="row"
                      className="text-grey89 fw-normal fs-14 font-primary"
                    >
                      Grand Total
                    </th>
                    <th className="text-end fw-medium fs-14 font-primary text-black2D">
                      £{Number(totals?.grandTotal ?? 0).toFixed(2)}
                    </th>
                  </tr>
                  {/* Add Deposit */}
                  {depositDetails?.type ? (
                    <tr className="d-flex justify-content-between gap-5">
                      <td className="text-grey89 fw-normal fs-14 font-primary">
                        Deposit (
                        {depositDetails.type === "Fixed" ? "Fixed" : `${depositDetails.amount}%`}
                        )
                      </td>
                      <td className="text-end d-flex align-items-center justify-content-end gap-2">
                        <>
                          <th className="text-end fw-medium fs-14 font-primary text-black2D">
                            £{Number(depositDetails.type === "Fixed" ? depositDetails?.amount : (totals?.grandTotal * depositDetails?.amount) / 100).toFixed(2)}
                          </th>
                          {isDepositEditable && (
                            <>
                              <span
                                className="text-primary cursor-pointer"
                                title="Edit deposit"
                                onClick={toggleDepositModal}
                              >
                                <Icon name="Edit" />
                              </span>
                              <span
                                className="text-danger cursor-pointer"
                                title="Remove deposit"
                                onClick={() => setDepositDetails(null)}
                              >
                                <Icon name="Delete" />
                              </span>
                            </>
                          )}
                        </>

                      </td>
                    </tr>
                  ) : (
                    isDepositEditable && (
                      <tr className="d-flex justify-content-between gap-5">
                        <td className="text-grey89 fw-normal fs-14 font-primary">Deposit</td>
                        <td className="text-end">
                          <p
                            className="text-primary fw-normal fs-14 font-primary cursor-pointer mb-0 pb-0"
                            onClick={toggleDepositModal}
                          >
                            + Add Deposit
                          </p>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </td>
          </div>
        </Col>
      </div>

      <ItemsFilter
        onCloseClick={() => setShowItemFilter(false)}
        show={showItemFilter}
        onFilterChange={(selectedCategory, selectedSubcategory) => {
          setSelectedCategory(selectedCategory);
          setSelectedSubcategory(selectedSubcategory);
          setShowItemFilter(false);
        }}
      />

      <ManageItemModal
        showModal={showManageItemModal}
        toggle={toggleManageItemModal}
        isEdit={isEditItemModal}
        onAddCategoryClick={toggleAddCategoryModal}
        onAddSubCategoryClick={toggleAddSubCategoryModal}
        selectedItem={selectedItem}
        quoteDetails={quoteDetails}
        invoiceDetails={null}
        presetQuoteDetails={null}
      />

      <AddCategoryModal
        show={showAddCategoryModal}
        onCloseClick={toggleAddCategoryModal}
      />

      <AddSubCategoryModal
        show={showAddSubCategoryModal}
        onCloseClick={toggleAddSubCategoryModal}
      />

      <DiscountModal
        openDiscountModal={openDiscountModal}
        toggleDiscountModal={toggleDiscountModal}
        discountPercentage={discount}
        addDiscount={(discount: number) => {
          setDiscount(discount);
          toggleDiscountModal();
        }}
      />
      <TaxModal
        openTaxModal={openTaxModal}
        toggleTaxModal={() => setOpenTaxModal((p) => !p)}
        taxPercentage={tax}
        rateOptions={taxOptions}
        addTax={(tax: number) => {
          setTax(tax);
          setOpenTaxModal(false);
        }}
      />

      {/* <MarginModal
        openMarginModal={openMarginModal}
        toggleMarginModal={toggleMarginModal}
      /> */}
      <CheckMarginModal
        isOpen={openMarginModal}
        onClose={toggleMarginModal}
        items={addItems}
      />

      <DeleteItemModal
        show={deleteModal !== null}
        onCloseClick={() => setDeleteModal(null)}
        onDeleteClick={() => {
          if (deleteModal) {
            const itemId = deleteModal;
            dispatch(deleteItem(itemId));
          }
          setDeleteModal(null);
        }}
      />

      <AddDepositModal
        isOpen={openDepositModal}
        onClose={toggleDepositModal}
        grandTotal={totals?.grandTotal}
        initialType={depositDetails?.type}
        initialAmount={depositDetails?.amount}
        initialPaymentMethod={depositDetails?.paymentMethod}
        onAdd={(type, amount, paymentMethod) => {
          if (user?.stripe_account_status !== "active" && paymentMethod === "stripe") {
            toggleDepositModal();
            setIsShowStripeConnectModal(true);
            return;
          }
          setDepositDetails({ type, amount, paymentMethod });
          toggleDepositModal();
        }}
      />

      <RestrictedAccessModal
        isOpen={isShowStripeConnectModal}
        onCancel={() => setIsShowStripeConnectModal(false)}
        title="Payments & Processing Fees"
        description={`Online payments are securely processed through Stripe.
   
   A 0.5% platform processing fee applies to all card and online payments processed through Stripe, including deposit payments. Standard Stripe transaction fees may also apply separately.
   
   These fees are deducted internally from your payout, your customers will always pay the exact invoice or deposit amount shown to them.
   
   Platform fees do not apply to cash payments, bank transfers, or offline payments.`}
        buttonText="Connect Stripe"
        onButtonClick={() => {
          setIsShowStripeConnectModal(false);
          navigate("/settings", { state: { focusStripe: true } });
        }}
        bodyClassName="text-start align-content-start"
        cancelText="Not Now"
      />
    </>
  );
};

export default React.memo(ItemsPane);
